/**
 * API utility for making HTTP requests with proper base URL handling
 */

// Get the API base URL from environment variable or default to localhost for development
const getApiBaseUrl = (): string => {
  // In production (Heroku), use relative URLs since frontend and backend are on same domain
  // In development, use localhost:5000
  if (process.env.NODE_ENV === 'production') {
    return ''; // Use relative URLs in production
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Make an authenticated API request
 */
export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {},
  token?: string | null
): Promise<Response> => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const requestOptions: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  return fetch(url, requestOptions);
};

/**
 * Make a GET request to the API
 */
export const apiGet = (endpoint: string, token?: string | null): Promise<Response> => {
  return apiRequest(endpoint, { method: 'GET' }, token);
};

/**
 * Make a POST request to the API
 */
export const apiPost = (
  endpoint: string,
  data?: any,
  token?: string | null
): Promise<Response> => {
  return apiRequest(
    endpoint,
    {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    },
    token
  );
};

/**
 * Make a PUT request to the API
 */
export const apiPut = (
  endpoint: string,
  data?: any,
  token?: string | null
): Promise<Response> => {
  return apiRequest(
    endpoint,
    {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    },
    token
  );
};

/**
 * Make a DELETE request to the API
 */
export const apiDelete = (endpoint: string, token?: string | null): Promise<Response> => {
  return apiRequest(endpoint, { method: 'DELETE' }, token);
};
